/**
 * The filter primitives: operators, ranges, and negation.
 *
 * Kept pure and separate from the table's React state so the semantics can be
 * pinned down by tests — a filter that quietly matches the wrong rows is worse
 * than one that errors, because the list still looks plausible.
 *
 * Ranges are additive rather than a replacement for the value dropdowns. A
 * seller ticking "90 – 100" is faster than typing a bound, and the dropdowns
 * carry live counts that a range cannot; both apply together (AND), so nothing
 * that worked before stops working.
 */

// ── text ────────────────────────────────────────────────────────────────────

export type TextOp = 'contains' | 'notContains' | 'equals' | 'startsWith' | 'empty' | 'notEmpty'

export const TEXT_OPS: { op: TextOp; label: string; needsValue: boolean }[] = [
  { op: 'contains', label: 'innehåller', needsValue: true },
  { op: 'notContains', label: 'innehåller inte', needsValue: true },
  { op: 'equals', label: 'är exakt', needsValue: true },
  { op: 'startsWith', label: 'börjar med', needsValue: true },
  { op: 'empty', label: 'är tomt', needsValue: false },
  { op: 'notEmpty', label: 'är inte tomt', needsValue: false },
]

export interface TextFilter {
  op: TextOp
  value: string
}

export const EMPTY_TEXT: TextFilter = { op: 'contains', value: '' }

const needsValue = (op: TextOp) => op !== 'empty' && op !== 'notEmpty'

/** An operator that needs a term does nothing until it has one — otherwise
 *  choosing "does not contain" with an empty box would hide every row. */
export function isTextActive(f: TextFilter): boolean {
  return needsValue(f.op) ? f.value.trim().length > 0 : true
}

export function matchesText(raw: string | null | undefined, f: TextFilter): boolean {
  const hay = (raw || '').trim().toLowerCase()
  if (f.op === 'empty') return hay === ''
  if (f.op === 'notEmpty') return hay !== ''
  const needle = f.value.trim().toLowerCase()
  if (!needle) return true
  switch (f.op) {
    case 'contains': return hay.includes(needle)
    case 'notContains': return !hay.includes(needle)
    case 'equals': return hay === needle
    case 'startsWith': return hay.startsWith(needle)
  }
}

// ── categorical, with negation ──────────────────────────────────────────────

export type SetMode = 'include' | 'exclude'

export interface SetFilter {
  mode: SetMode
  values: Set<string>
}

export const emptySet = (): SetFilter => ({ mode: 'include', values: new Set() })

export const isSetActive = (f: SetFilter): boolean => f.values.size > 0

/** Exclude is what makes "everything except Disqualified" one click instead of
 *  ticking the other seven — and it keeps meaning that when a new stage is
 *  added, which an include-list silently would not. */
export function matchesSet(value: string, f: SetFilter): boolean {
  if (f.values.size === 0) return true
  const hit = f.values.has(value)
  return f.mode === 'exclude' ? !hit : hit
}

// The dropdown's edits. A tick is always "this value is in the set", in both
// modes; the mode only decides what the set means. So "Alla utom" with nothing
// ticked shows every row, ticking X hides X, and switching mode keeps the
// ticks where they are instead of flipping every box.

export function isTicked(f: SetFilter, value: string): boolean {
  return f.values.has(value)
}

export function toggleValue(f: SetFilter, value: string): SetFilter {
  const values = new Set(f.values)
  if (values.has(value)) values.delete(value)
  else values.add(value)
  return { mode: f.mode, values }
}

export function setMode(f: SetFilter, mode: SetMode): SetFilter {
  return { mode, values: new Set(f.values) }
}

export function tickValues(f: SetFilter, values: string[]): SetFilter {
  const next = new Set(f.values)
  values.forEach(v => next.add(v))
  return { mode: f.mode, values: next }
}

export function untickValues(f: SetFilter, values: string[]): SetFilter {
  const next = new Set(f.values)
  values.forEach(v => next.delete(v))
  return { mode: f.mode, values: next }
}

// ── numbers ─────────────────────────────────────────────────────────────────

export type NumOp = 'any' | 'gte' | 'lte' | 'between' | 'equals'

export const NUM_OPS: { op: NumOp; label: string; bounds: 0 | 1 | 2 }[] = [
  { op: 'any', label: 'alla', bounds: 0 },
  { op: 'gte', label: 'minst', bounds: 1 },
  { op: 'lte', label: 'högst', bounds: 1 },
  { op: 'between', label: 'mellan', bounds: 2 },
  { op: 'equals', label: 'exakt', bounds: 1 },
]

export interface NumFilter {
  op: NumOp
  a: number | null
  b: number | null
}

export const EMPTY_NUM: NumFilter = { op: 'any', a: null, b: null }

export function isNumActive(f: NumFilter): boolean {
  if (f.op === 'any') return false
  if (f.op === 'between') return f.a != null && f.b != null
  return f.a != null
}

export function matchesNum(n: number | null | undefined, f: NumFilter): boolean {
  if (!isNumActive(f)) return true
  if (n == null) return false
  const a = f.a as number
  switch (f.op) {
    case 'gte': return n >= a
    case 'lte': return n <= a
    case 'equals': return n === a
    // Written so an inverted range still selects the span the user drew,
    // rather than nothing: typing 90 then 60 means the same as 60 then 90.
    case 'between': {
      const b = f.b as number
      return n >= Math.min(a, b) && n <= Math.max(a, b)
    }
    default: return true
  }
}

// ── dates ───────────────────────────────────────────────────────────────────

export type DateOp = 'any' | 'after' | 'before' | 'between' | 'empty' | 'notEmpty'

export const DATE_OPS: { op: DateOp; label: string; bounds: 0 | 1 | 2 }[] = [
  { op: 'any', label: 'alla', bounds: 0 },
  { op: 'after', label: 'på eller efter', bounds: 1 },
  { op: 'before', label: 'på eller före', bounds: 1 },
  { op: 'between', label: 'mellan', bounds: 2 },
  { op: 'empty', label: 'inget datum', bounds: 0 },
  { op: 'notEmpty', label: 'har ett datum', bounds: 0 },
]

export interface DateFilter {
  op: DateOp
  /** yyyy-mm-dd, as the date inputs produce. */
  from: string | null
  to: string | null
}

export const EMPTY_DATE: DateFilter = { op: 'any', from: null, to: null }

export function isDateActive(f: DateFilter): boolean {
  if (f.op === 'any') return false
  if (f.op === 'empty' || f.op === 'notEmpty') return true
  if (f.op === 'between') return !!f.from && !!f.to
  return !!f.from
}

/** Compared as calendar days, not instants.
 *
 * The bounds come from a date input, so "on or before the 30th" has to include
 * everything that happened during the 30th — comparing timestamps would drop a
 * meeting booked that afternoon. Day keys are taken in local time, matching how
 * the same dates are rendered in the row. */
function dayKey(iso: string): string | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function matchesDate(iso: string | null | undefined, f: DateFilter): boolean {
  if (!isDateActive(f)) return true
  const key = iso ? dayKey(iso) : null
  if (f.op === 'empty') return key === null
  if (f.op === 'notEmpty') return key !== null
  if (key === null) return false
  switch (f.op) {
    case 'after': return key >= (f.from as string)
    case 'before': return key <= (f.from as string)
    case 'between': {
      const lo = (f.from as string) <= (f.to as string) ? f.from : f.to
      const hi = (f.from as string) <= (f.to as string) ? f.to : f.from
      return key >= (lo as string) && key <= (hi as string)
    }
    default: return true
  }
}
